import { motion, useInView } from 'framer-motion';
import { useInViewScroll } from 'ui';
import { XumoForm } from './Form';
import { InputType } from '../types';
import { useSanitizedTranslation } from '../hooks';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';

interface ProspectProps {
  headingBold?: string;
  heading?: string;
  body?: string;
  successMessage?: string;
  errorMessage?: string;
  ctaLabel?: string;
  isModal?: boolean;
  daysHidden?: number;
  heroImage?: string;
  heroAlt?: string;
  id?: string;
  hideGradient?: boolean;
  verticalStack?: boolean;
}

export const Prospect = ({
  headingBold,
  heading,
  body,
  successMessage,
  errorMessage,
  ctaLabel,
  isModal = false,
  daysHidden = 7,
  heroImage,
  heroAlt = 'Promotional image',
  id,
  hideGradient = false,
  verticalStack = false,
}: ProspectProps) => {
  const { t } = useSanitizedTranslation();
  const router = useRouter();
  const slugifiedPath =
    id ??
    router.asPath
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const { ref, progress: amt } = useInViewScroll([0, 1], ['start 100vh', 'end 0vh']);
  const inView = useInView(ref, { once: true, margin: '-30% 0% -30% 0%' });

  const [showModal, setShowModal] = useState(false);
  const hasBeenClosed = useRef(false);
  const scrollHandlerRef = useRef<() => void>();

  const cleanupScrollListener = useCallback(() => {
    if (scrollHandlerRef.current) {
      window.removeEventListener('scroll', scrollHandlerRef.current);
    }
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    hasBeenClosed.current = true;
    cleanupScrollListener();
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }, [cleanupScrollListener]);

  const handleHideModal = useCallback(() => {
    const thirtyDaysInMs = daysHidden * 24 * 60 * 60 * 1000;
    const expiryTime = new Date().getTime() + thirtyDaysInMs;
    localStorage.setItem(slugifiedPath + '-prospectModalHiddenUntil', expiryTime.toString());
    setShowModal(false);
    hasBeenClosed.current = true;
    cleanupScrollListener();
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }, [cleanupScrollListener, daysHidden, slugifiedPath]);

  useEffect(() => {
    if (!isModal) return;

    const checkScrollAndCookie = () => {
      if (hasBeenClosed.current) return;

      const hiddenUntil = localStorage.getItem(slugifiedPath + '-prospectModalHiddenUntil');
      const now = new Date().getTime();

      if (hiddenUntil && parseInt(hiddenUntil) > now) {
        return;
      }

      const scrollPosition = window.scrollY;
      const pageHeight = document.body.scrollHeight - window.innerHeight;
      const scrollPercentage = pageHeight > 0 ? (scrollPosition / pageHeight) * 100 : 0;

      if (scrollPercentage >= 20) {
        setShowModal(true);
        cleanupScrollListener();
      }
    };

    let timeout: ReturnType<typeof setTimeout>;
    const handleScroll = () => {
      clearTimeout(timeout);
      timeout = setTimeout(checkScrollAndCookie, 100);
    };

    scrollHandlerRef.current = handleScroll;
    window.addEventListener('scroll', handleScroll);

    return () => {
      cleanupScrollListener();
      clearTimeout(timeout);
    };
  }, [isModal, cleanupScrollListener]);

  useEffect(() => {
    if (isModal && showModal) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      if (isModal) {
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
      }
    };
  }, [isModal, showModal]);

  // Modal version of the component
  if (isModal) {
    if (!showModal) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
        <div className="relative max-h-[90vh] w-full max-w-4xl overflow-auto rounded-lg bg-white shadow-xl">
          <div className="absolute right-4 top-4 z-50 flex items-center">
            {/* <button onClick={handleHideModal} className="mr-2 px-4 text-sm text-gray-700">
              Don't show again
            </button> */}
            <button onClick={handleHideModal} className="flex h-8 w-8 items-center justify-center rounded-sm text-gray-700">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>

          <div className="flex flex-col md:h-full md:flex-row">
            {heroImage && (
              <div className="relative h-64 w-full overflow-hidden md:h-auto md:w-1/2">
                <div className="relative h-full w-full">
                  <Image src={heroImage} alt={heroAlt} layout="fill" objectFit="cover" objectPosition="center" priority />
                </div>
              </div>
            )}

            <div className={`flex w-full flex-col p-6 pt-16 md:p-10 ${heroImage ? 'md:w-1/2' : 'md:w-full'}`}>
              <div className="mb-6">
                <div className="text-center text-2xl md:text-left" data-i18n>
                  <strong>{t(headingBold ?? 'Level up')}</strong> <span className="font-extralight">{t(heading ?? 'your\u00A0inbox')}</span>
                </div>
                <div className="mt-2 text-center md:text-left" data-i18n>
                  {t(body ?? 'Sign up for emails from Xumo to keep up with all the latest shows and movies, new products and exclusive offers.')}
                </div>
              </div>

              <XumoForm
                formFields={[
                  { name: 'first_name', type: InputType.Text, placeholder: 'First Name', width: 'full' },
                  { name: 'last_name', type: InputType.Text, placeholder: 'Last Name', width: 'full' },
                  { name: 'email', type: InputType.Email, placeholder: 'Email', width: 'full' },
                ]}
                CTA={{ label: ctaLabel ?? "Let's do this", href: '#' }}
                formAction={{
                  submissionEndpoint: '/api/prospect',
                  successMessage: successMessage ?? 'You have successfully subscribed',
                  errorMessage: errorMessage ?? 'Unable to subscribe',
                }}
                styleOverrides={{
                  form: `justify-top items-center w-full`,
                  formField: `focus:border-b-xumoBerry focus:border-opacity-[100%] rounded-none mt-5 p-4 placeholder:text-black outline-1`,
                  hideLabel: true,
                  CTA: {
                    general: `border-0 text-white px-6 mt-6 py-2 w-min`,
                    disabledColor: 'bg-xumoCharcoal',
                    enabledColor: 'bg-xumoBerry',
                    alignment: `${verticalStack ? 'self-center' : 'self-start'} mt-4 w-full md:w-auto`,
                  },
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Original inline version of the component
  return (
    <motion.section
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: inView ? 1 : 0, y: inView ? 0 : 40 }}
      transition={{ ease: [0, 1, 1, 1] }}
      ref={ref}
      className={`relative z-40 w-full ${hideGradient ? 'bg-white' : 'bg-gradient-to-b from-[#5B2E4F] to-[#150E14]'}`}
    >
      <div className="p-[50px] md:p-[100px]">
        <div className={`flex flex-col gap-[10px] self-stretch bg-white ${!verticalStack ? 'md:flex-row' : ''}`}>
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 100 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-30% 0% -30% 0%' }}
            className={`justify-top flex w-full flex-col px-10 pt-10 [filter:revert] ${!verticalStack ? 'md:w-1/2' : 'text-center'} md:p-10`}
          >
            <div className={verticalStack ? 'text-2xl md:text-4xl lg:text-5xl' : 'text-2xl'} data-i18n>
              <strong>{t(headingBold ?? 'Level up')}</strong> <span className="font-extralight"> {t(heading ?? 'your\u00A0inbox')}</span>
            </div>
            <div className="mt-2" data-i18n>
              {t(body ?? 'Sign up for emails from Xumo to keep up with all the latest shows and movies, new products and exclusive offers.')}
            </div>
          </motion.div>
          <XumoForm
            formFields={[
              { name: 'first_name', type: InputType.Text, placeholder: 'First Name', width: 'half' },
              { name: 'last_name', type: InputType.Text, placeholder: 'Last Name', width: 'half' },
              { name: 'email', type: InputType.Email, placeholder: 'Email', width: 'full' },
            ]}
            CTA={{ label: ctaLabel ?? "Let's do this", href: '#' }}
            formAction={{
              submissionEndpoint: '/api/prospect',
              successMessage: successMessage ?? 'You have successfully subscribed',
              errorMessage: errorMessage ?? 'Unable to subscribe',
            }}
            styleOverrides={{
              form: `justify-top items-center w-full ${!verticalStack ? 'md:w-1/2' : ''} p-10 pt-5`,
              formField: `focus:border-b-xumoBerry focus:border-opacity-[100%] rounded-none mt-5 p-4 placeholder:text-black outline-1`,
              hideLabel: true,
              CTA: {
                general: `border-0 text-white px-6 mt-6 py-2 w-min`,
                disabledColor: 'bg-xumoCharcoal',
                enabledColor: 'bg-xumoBerry',
                alignment: `${verticalStack ? 'self-center' : 'self-start'} mt-4 w-full md:w-auto`,
              },
            }}
          />
        </div>
      </div>
    </motion.section>
  );
};
